import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { Users, TrendingUp, Clock, MousePointer, Filter, Download } from 'lucide-react';
import { useSession } from '@clerk/clerk-react';
import DetailedAnalyticsService from '../../../services/dashboard/detailedAnalyticsService';

const SessionsAnalysis = ({ companyId }) => {
  const { session } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [selectedChannel, setSelectedChannel] = useState('all');

  useEffect(() => {
    if (session) {
      loadSessionsData();
    }
  }, [companyId, dateRange, session]);

  const loadSessionsData = async () => {
    if (!session) return;
    
    setLoading(true);
    try {
      const analyticsService = new DetailedAnalyticsService(session);
      const sessionsData = await analyticsService.getSessionsData(companyId, dateRange);
      setData(sessionsData);
    } catch (error) {
      console.error('Error loading sessions data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChannelColor = (channel) => {
    const colors = {
      web: '#3b82f6',
      mobile: '#158B5C',
      email: '#f59e0b',
      social: '#8b5cf6',
      direct: '#ef4444'
    };
    return colors[channel] || '#6b7280';
  };

  const filteredSessions = selectedChannel === 'all' 
    ? data?.sessions || []
    : (data?.sessions || []).filter(session => session.channel === selectedChannel);

  const channelData = data?.channelStats ? Object.entries(data.channelStats).map(([channel, stats]) => ({
    channel,
    total: stats.total,
    conversions: stats.conversions,
    conversionRate: stats.total > 0 ? (stats.conversions / stats.total) * 100 : 0
  })) : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controles */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <Select value={dateRange.toString()} onValueChange={(value) => setDateRange(parseInt(value))}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={selectedChannel} onValueChange={setSelectedChannel}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Canal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los canales</SelectItem>
              {Object.keys(data?.channelStats || {}).map(channel => (
                <SelectItem key={channel} value={channel}>
                  {channel.charAt(0).toUpperCase() + channel.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Sesiones</p>
                <p className="text-2xl font-bold">{data?.summary?.totalSessions || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Conversiones</p>
                <p className="text-2xl font-bold">{data?.summary?.conversions || 0}</p>
              </div>
              <MousePointer className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tasa Conversión</p>
                <p className="text-2xl font-bold">{data?.summary?.conversionRate || 0}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Duración Promedio</p>
                <p className="text-2xl font-bold">{data?.summary?.avgDuration || 0}m</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos y análisis */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends">Tendencias</TabsTrigger>
          <TabsTrigger value="channels">Canales</TabsTrigger>
          <TabsTrigger value="details">Detalles</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tendencia de Sesiones y Conversiones</CardTitle>
              <CardDescription>
                Evolución diaria de sesiones y conversiones en los últimos {dateRange} días
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data?.chartData || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="sessions" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="Sesiones"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="conversions" 
                    stroke="#158B5C" 
                    strokeWidth={2}
                    name="Conversiones"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="channels" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Distribución por Canal</CardTitle>
                <CardDescription>Sesiones por canal de origen</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={channelData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="total"
                      label={({ channel, total }) => `${channel}: ${total}`}
                    >
                      {channelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getChannelColor(entry.channel)} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Conversión por Canal</CardTitle>
                <CardDescription>Tasa de conversión por canal</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={channelData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="channel" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Tasa de Conversión']} />
                    <Bar dataKey="conversionRate" fill="#158B5C" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Detalle de Sesiones</CardTitle>
              <CardDescription>
                Lista detallada de sesiones {selectedChannel !== 'all' && `del canal ${selectedChannel}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Fecha</th>
                      <th className="text-left p-2">Canal</th>
                      <th className="text-left p-2">Duración</th>
                      <th className="text-left p-2">Conversión</th>
                      <th className="text-left p-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.slice(0, 50).map((session) => (
                      <tr key={session.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          {new Date(session.created_at).toLocaleDateString('es-CL')}
                        </td>
                        <td className="p-2">
                          <Badge variant="outline" style={{ borderColor: getChannelColor(session.channel) }}>
                            {session.channel}
                          </Badge>
                        </td>
                        <td className="p-2">{session.duration}m</td>
                        <td className="p-2">
                          {session.hasQuotation ? (
                            <Badge variant="default" className="bg-green-100 text-green-800">
                              Sí
                            </Badge>
                          ) : (
                            <Badge variant="secondary">No</Badge>
                          )}
                        </td>
                        <td className="p-2">
                          {session.quotationStatus && (
                            <Badge variant="outline">
                              {session.quotationStatus}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredSessions.length > 50 && (
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Mostrando 50 de {filteredSessions.length} sesiones
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SessionsAnalysis;